import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import ImmutablePropTypes from "react-immutable-proptypes";
import { AnalyticsCount } from "resourceModels/index";
import { getResource, getResources } from "selectors/resources";
import { closeModalAction } from "actions/modal";
import Button from "components/Common/Button";
import SvgIcon from "components/Common/SvgIcon";
import colors from "stylesheets/utilities/colors.scss";
import StartImage from "./images/Year-in-number-Start.png";
import ThanksImage from "./images/Year-in-number-Thanks.png";
import { metricModalStyles, wrapUpReportRequestParams } from "./services";

import "./style.scss";

const BLOCK_NAME = "AnalyticsYearEndWrapUpReportModal";

/**
 * @param {Number} num
 * @returns {*}
 */
function kFormatter(num) {
  return Math.round(num) > 99999999
    ? `${Math.round(num / 1000000).toLocaleString()}M` : num.toLocaleString();
}

class AnalyticsYearEndWrapUpReportModal extends React.PureComponent {
  handleContinueButtonClickBound = this.handleContinueButtonClick.bind(this);
  handleCloseButtonClickBound = this.handleCloseButtonClick.bind(this);
  circleRef = React.createRef();
  state = {
    screenNumber: 0
  };

  /**
   */
  componentDidUpdate() {
    this.updateElementsStyle();
  }

  /**
   * Methods
   */

  /**
   */
  updateElementsStyle() {
    const { screenNumber } = this.state;

    if (screenNumber === 0) {
      return;
    }

    const circleAttributes = metricModalStyles[screenNumber - 1].circle;

    requestAnimationFrame(() => {
      this.circleRef.current.setAttribute(
        "style",
        `background-color: ${circleAttributes.color};
         top: ${circleAttributes.top}px;
         right: ${circleAttributes.right}px;
         width: ${circleAttributes.diameter}px;
         height: ${circleAttributes.diameter}px`
      );
    });
  }

  /**
   */
  handleContinueButtonClick() {
    const { screenNumber } = this.state;

    this.setState({
      screenNumber: screenNumber + 1
    });
  }

  /**
   */
  handleCloseButtonClick() {
    const { closeModal } = this.props;

    closeModal();
  }

  /**
   * @returns {ReactElement}
   */
  renderWelcomeScreen() {
    const { screenNumber } = this.state;

    return (
      <div
        className={classNames(`${BLOCK_NAME}__welcome-screen`, {
          [`${BLOCK_NAME}__welcome-screen--hidden`]: screenNumber !== 0
        })}
      >
        <SvgIcon
          customClassName={`${BLOCK_NAME}__brand-icon`}
          icon="AdaWhite"
          fillColor={colors.white}
        />
        <div className={`${BLOCK_NAME}__welcome-screen-image-container`}>
          <img className={`${BLOCK_NAME}__welcome-screen-image`} src={StartImage} alt="Welcome" />
        </div>
        <div className={`${BLOCK_NAME}__welcome-screen-title`}>Your year in numbers</div>
        <div className={`${BLOCK_NAME}__welcome-screen-description`}>
          Let’s explore some of the data that made 2019 exceptional
        </div>
        <Button
          customClassName={`${BLOCK_NAME}__button`}
          onClick={this.handleContinueButtonClickBound}
          text="Start"
        />
      </div>
    );
  }

  /**
   * @returns {ReactElement}
   */
  renderThanksScreen() {
    const { clientName } = this.props;
    const { screenNumber } = this.state;

    return (
      <div
        className={classNames(`${BLOCK_NAME}__thanks-screen`, {
          [`${BLOCK_NAME}__thanks-screen--visible`]: screenNumber === 5
        })}
      >
        <SvgIcon
          customClassName={`${BLOCK_NAME}__brand-icon`}
          icon="AdaWhite"
        />
        <div className={`${BLOCK_NAME}__thanks-screen-image-container`}>
          <img className={`${BLOCK_NAME}__welcome-screen-image`} src={ThanksImage} alt="Welcome" />
        </div>
        <div className={`${BLOCK_NAME}__welcome-screen-title`}>Thanks,&nbsp;{clientName}</div>
        <div className={`${BLOCK_NAME}__welcome-screen-description`}>
          for being a leader in customer experience
        </div>
        <Button
          customClassName={`${BLOCK_NAME}__button--thanks-screen`}
          text="Close"
          onClick={this.handleCloseButtonClickBound}
        />
      </div>
    );
  }

  /**
   * @returns {ReactElement}
   */
  renderMetricsScreen() {
    const { wrapUpReportCounts } = this.props;
    const { screenNumber } = this.state;

    return (
      <div
        className={classNames(`${BLOCK_NAME}__metrics-screen`, {
          [`${BLOCK_NAME}__metrics-screen--visible`]: screenNumber > 0 && screenNumber < 5
        })}
      >
        <SvgIcon
          customClassName={`${BLOCK_NAME}__brand-icon`}
          icon="Ada"
        />
        {wrapUpReportCounts.map((data, i) => (
          <div key={data.label}>
            <div
              style={{
                top: metricModalStyles[i].text.top
              }}
              className={classNames(`${BLOCK_NAME}__metrics-screen-text-container`, {
                [`${BLOCK_NAME}__metrics-screen-text-container--visible`]: screenNumber === i + 1
              })}
            >
              <div className={`${BLOCK_NAME}__metrics-screen-title`}>
                {metricModalStyles[i].text.title}
              </div>
              <div>
                {metricModalStyles[i].text.description}
              </div>
            </div>
            <div
              style={{
                top: metricModalStyles[i].image.top,
                right: metricModalStyles[i].image.right,
                height: metricModalStyles[i].image.height,
                width: metricModalStyles[i].image.width
              }}
              className={classNames(`${BLOCK_NAME}__metrics-screen-image-container`, {
                [`${BLOCK_NAME}__metrics-screen-image-container--visible`]: screenNumber === i + 1
              })}
            >
              <img
                className={`${BLOCK_NAME}__metrics-screen-image`}
                src={metricModalStyles[i].image.src}
                alt="featured images"
              />
            </div>
            <div
              style={{
                top: screenNumber <= i + 1 ? metricModalStyles[i].value.top
                  : metricModalStyles[i].value.nextTop
              }}
              className={classNames(`${BLOCK_NAME}__metrics-screen-value`, {
                [`${BLOCK_NAME}__metrics-screen-value--visible`]: screenNumber === i + 1
              })}
            >
              {kFormatter(data.value)}
            </div>
            <div
              className={classNames(`${BLOCK_NAME}__metrics-screen-note`, {
                [`${BLOCK_NAME}__metrics-screen-note--visible`]: screenNumber === 4
              })}
            >
              <span>
                *Based on an average call length of 5.97 minutes
                (
                <a
                  className={`${BLOCK_NAME}__inline-link`}
                  target="_blank"
                  rel="noopener noreferrer"
                  href="http://customerthink.com/customer_service_by_the_numbers_average_call_duration/"
                >
                  source
                </a>
                )
              </span>
            </div>
          </div>
        ))}
        <Button
          customClassName={`${BLOCK_NAME}__button ${BLOCK_NAME}__button--metrics-screen`}
          onClick={this.handleContinueButtonClickBound}
          text="Continue"
        />
      </div>
    );
  }

  /**
   */
  render() {
    const { screenNumber } = this.state;

    return (
      <div className={BLOCK_NAME}>
        {this.renderWelcomeScreen()}
        {this.renderThanksScreen()}
        {this.renderMetricsScreen()}
        <div
          className={classNames(`${BLOCK_NAME}__animated-circle`, {
            [`${BLOCK_NAME}__animated-circle--visible`]: screenNumber > 0
          })}
          ref={this.circleRef}
        />
        {screenNumber > 0 && (
          <div
            style={{
              backgroundColor: metricModalStyles[screenNumber - 1].backgroundColor
            }}
            className={`${BLOCK_NAME}__background`}
          />
        )}
      </div>
    );
  }
}

AnalyticsYearEndWrapUpReportModal.hasOpacityTransition = true;
AnalyticsYearEndWrapUpReportModal.closeButtonColor = colors.colorWhite;
AnalyticsYearEndWrapUpReportModal.propTypes = {
  clientName: PropTypes.string.isRequired,
  wrapUpReportCounts: ImmutablePropTypes.listOf(PropTypes.instanceOf(AnalyticsCount)).isRequired,
  closeModal: PropTypes.func.isRequired
};

/**
 * @param {Object} state
 * @returns {Object}
 */
function mapStateToProps(state) {
  const client = getResource(state, "client");

  return {
    clientName: client.name,
    wrapUpReportCounts: getResources(state, "analyticsCount", wrapUpReportRequestParams)
  };
}

/**
 * @param {Function} dispatch
 * @returns {Object.<Function>}
 */
function mapDispatch(dispatch) {
  return {
    closeModal: () => dispatch(closeModalAction())
  };
}

export default connect(mapStateToProps, mapDispatch)(AnalyticsYearEndWrapUpReportModal);
